import { Client } from "@/Common/Api/Client";
import { Employee } from "@/Common/Api/Employee/Dto/Employee";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { LicenceOrganisationSpecialisation } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationSpecialisation";
import { PatientRefIntervenorWithDetails } from "@/Common/Api/Patient/Dto/PatientRefIntervenorWithDetails";
import { GetResponsibleProvidersResponse } from "@/Common/Api/Provider/Dto/Responses/GetResponsibleProvidersResponse";
import { ClinicianSearchStrategy, CustomSortClinicianSearchStrategy } from "@/Common/Components/Search/PersonSearchAutocompleteCustom";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { PatientRefIntervenorWithDetailsModel } from "@/Common/Models/Patient/PatientRefIntervenorWithDetailsModel";
import { Person, PersonSourceContext } from "@/Common/Models/Person/Person";
import { RAMQInfo } from "@/Common/Models/RAMQ/RAMQInfo";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { Server } from "@/Common/Operations/operations.server";
import { RAMQClinicianRetriever } from "./RAMQClinicianRetriever";

class CliniciansHelper {
    public static async getSortedPatientClinicianSearchStrategy(patient: Person, includeEnrolledResidents: boolean = false): Promise<ClinicianSearchStrategy> {
        const searchStrategyClinicians = await this.getSortedPatientClinicians(patient, includeEnrolledResidents);

        return searchStrategyClinicians.any()
            ? new ClinicianSearchStrategy(searchStrategyClinicians)
            : this.GetAllCliniciansSearchStrategy();
    }

    public static async getPatientAndAllCliniciansSearchStrategy(patient: Person): Promise<ClinicianSearchStrategy> {
        const patientClinicians = await this.getSortedPatientClinicians(patient, false);
        const allClinicians = cacheStore.GetClinicians(false).sortByProperty(p => p.LastName?.trim(), false);

        const concat = [...patientClinicians, ...allClinicians].distinctByProperty(p => p.ContactId);
        return new CustomSortClinicianSearchStrategy(concat);
    }

    public static async getStarredAndRamqRegisteredClinicians(patient: Person, includeDelegates: boolean = true): Promise<Person> {
        if (!patient?.ContactFacetId && !patient?.ContactId)
            return null;

        const allCliniciansForPatient = await this.getAllCliniciansForPatient(patient.ContactFacetId, includeDelegates);

        const primaryClinician: Person = this.getClinicianDetails(allCliniciansForPatient?.PrimaryResponsibleProvider);
        if (primaryClinician && !primaryClinician.IsInactive) {
            primaryClinician.SourceContext = PersonSourceContext.IsIntervenorOnFile;
            return primaryClinician;
        }

        const ramqRegisteredClinicians: Person[] = await this.getRamqCliniciansDetails(patient?.ContactId, true);
        if (ramqRegisteredClinicians && ramqRegisteredClinicians?.length > 0 && ramqRegisteredClinicians?.any(c => !c.IsInactive)) {
            const activeRegisteredClinician = ramqRegisteredClinicians.firstOrDefault(c => !c.IsInactive);
            activeRegisteredClinician.SourceContext = PersonSourceContext.IsRamqIntervenor;
            return activeRegisteredClinician;
        }

        const secondaryClinician: Person = this.getClinicianDetails(allCliniciansForPatient?.SecondaryResponsibleProvider);
        if (secondaryClinician && !secondaryClinician.IsInactive) {
            secondaryClinician.SourceContext = PersonSourceContext.IsIntervenorOnFile;
            return secondaryClinician;
        }

        return null;
    }

    public static GetAllCliniciansSearchStrategy(): ClinicianSearchStrategy {
        return new ClinicianSearchStrategy(cacheStore.GetClinicians(false));
    }

    private static async getSortedPatientClinicians(patient: Person, includeEnrolledResidents: boolean, includeDelegates: boolean = true): Promise<Person[]> {
        if (!patient.ContactFacetId && !patient.ContactId)
            return [];

        const response = await this.getAllCliniciansForPatient(patient.ContactFacetId, includeDelegates);
        const clinicians = response.ResponsibleProviders.map(p => new PatientRefIntervenorWithDetailsModel(p));
        const ramqClinicians = await this.getRamqCliniciansDetails(patient.ContactId, includeEnrolledResidents);
        const latestPhysicianWhoApprovedMedicalNote = this.getLatestPhysicianWhoApprovedMedicalNote(response.LatestPhysicianWhoApprovedMedicalNote);

        let sortedClinicians: Person[] = [];

        if (clinicians.any()) {
            sortedClinicians = this.sortCliniciansByCategoryThenSpecialty(clinicians.filter(clinician => clinician.IsFromClinic()))
                .map(clinician => this.convertRefIntervenorWithDetailsToPerson(clinician));

            sortedClinicians.forEach(p => p.SourceContext = PersonSourceContext.IsIntervenorOnFile);
        }

        if (!!ramqClinicians) {
            ramqClinicians.forEach(clinician => clinician.SourceContext = PersonSourceContext.IsRamqIntervenor);
            sortedClinicians = [...ramqClinicians, ...sortedClinicians];
        }

        if (latestPhysicianWhoApprovedMedicalNote && !sortedClinicians.any((p: Person) => p.ContactId === latestPhysicianWhoApprovedMedicalNote.ContactId)
        ) {
            latestPhysicianWhoApprovedMedicalNote.SourceContext = PersonSourceContext.HasLastMedicalNote;
            sortedClinicians.push(latestPhysicianWhoApprovedMedicalNote);
        }

        return sortedClinicians.distinctByProperty(p => p.ContactId);
    }

    private static async getAllCliniciansForPatient(patientContactFacetId: string, includeDelegates: boolean): Promise<GetResponsibleProvidersResponse> {
        const response = await Client.postAsync<GetResponsibleProvidersResponse>(
            Server.Myle_Faxing_GetResponsibleProviders,
            {
                PatientContactFacetId: patientContactFacetId,
                IncludeDelegates: includeDelegates
            }
        );
        return response.data;
    }

    private static async getRamqClinician(patientIndividualId: string): Promise<RAMQInfo> {
        if (sessionStore.clientConfiguration.HasMedfarRAMQ != null && sessionStore.clientConfiguration.HasMedfarRAMQ != true)
            return null;

        const response = await Client.postAsync<RAMQInfo>(
            Server.RAMQ_CheckSubscription,
            {
                Patient: {
                    IndividualID: patientIndividualId
                },
                CurrentUser: sessionStore.getCurrentEmployee().getRamqCredentials()
            }
        );

        return response.data;
    }

    private static sortCliniciansByCategoryThenSpecialty(clinicians: PatientRefIntervenorWithDetailsModel[]): PatientRefIntervenorWithDetailsModel[] {
        const sortedClinicians = clinicians
            .groupBy(clinician => clinician.GetSpecialisationCategoryName())
            .map((clinicians, categoryName) => {
                const items = clinicians
                    .sortByProperty(employee => employee.GetName())
                    .sortByProperty(employee => EmployeeTitleHelper.isDoctor(Employee.getTitle(employee.Employee.Model)), true);
                return new KeyValuePair(categoryName, items);
            })
            .sortByProperty(p => {
                if (p.Value[0].Model.LicenceOrganisationSpecialisation != null && p.Value[0].Model.LicenceOrganisationSpecialisation.SpecialisationCode == "0") // MD Famille
                    return -1;
                else if (p.Value.some(y => y.IsFromClinic() == true))
                    return 0;
                else
                    return p.Value[0].GetSpecialisationCategory().Position;
            });

        return [].concat(...sortedClinicians.map(p => p.Value));
    }

    private static convertRefIntervenorWithDetailsToPerson(clinician: PatientRefIntervenorWithDetailsModel): Person {
        const clinicianPerson = new Person({ ...cacheStore.GetCliniciansPersonsByContactFacetId(clinician.Employee.ContactFacetId) });

        const specialization = LicenceOrganisationSpecialisation.getSpecialtyName(clinician.Model.LicenceOrganisationSpecialisation);
        const licenceOrganisation = LicenceOrganisationRule.getTitleName(clinician.Model.LicenceOrganisationRule);
        const formattedSpecialtyAndTitle = `(${specialization} - ${licenceOrganisation})`;

        clinicianPerson.Name = `${clinician.Employee.Model.LastName}, ${clinician.Employee.Model.FirstName} ${formattedSpecialtyAndTitle}`;

        return clinicianPerson;
    }

    /**
     * Retrieves and orders RAMQ clinicians based on the following priority: (MYLE-9755)
     * 1. RAMQ IPS
     * 2. RAMQ Resident
     * 3. RAMQ Doctor
     *
     * @param patientContactId - The ID of the patient contact.
     * @param includeEnrolledResidents - Whether to include enrolled residents in the result.
     * @returns A list of clinicians ordered by priority.
     */
    private static async getRamqCliniciansDetails(patientContactId: string, includeEnrolledResidents: boolean = false): Promise<Person[]> {
        if (sessionStore.clientConfiguration.HasMedfarRAMQ != null && sessionStore.clientConfiguration.HasMedfarRAMQ === false)
            return null;

        const responseRamqClinician = await this.getRamqClinician(patientContactId);

        if (!responseRamqClinician)
            return null;

        const ramqRegisteredClinician = new RAMQInfo().FromResponseData(responseRamqClinician);
        return RAMQClinicianRetriever.getRamqClinicians(ramqRegisteredClinician, includeEnrolledResidents);
    }

    private static getClinicianDetails(starredProvider: PatientRefIntervenorWithDetails): Person {
        return starredProvider
            ? this.convertRefIntervenorWithDetailsToPerson(new PatientRefIntervenorWithDetailsModel(starredProvider))
            : null;
    }

    private static getLatestPhysicianWhoApprovedMedicalNote(physician: Employee): Person {
        return !physician
            ? null
            : this.convertPhysicianFromEmployeeToPerson(physician);
    }

    private static convertPhysicianFromEmployeeToPerson(physician: Employee): Person {
        const clinicianPerson = cacheStore.GetCliniciansPersonsByContactFacetId(physician.ContactFacet_id);
        const specialtyText = cacheStore.GetLookupById(physician.SpecialtyLookupId)?.GetValue() ?? "";

        const issuingLicenseOrganizationRuleId = physician.Licences?.firstOrDefault()?.IssuingLicenseOrganizationRuleId;
        const licenceOrganisationRule = issuingLicenseOrganizationRuleId ? cacheStore.LicenceOrganisationRulesById?.[issuingLicenseOrganizationRuleId] : null;
        const titleText = LicenceOrganisationRule.getTitleName(licenceOrganisationRule);
        const formattedSpecialtyAndTitle = this.getFormattedSpecialtyAndTitle(specialtyText, titleText);
        clinicianPerson.Name = `${clinicianPerson.FirstName}, ${clinicianPerson.LastName} ${formattedSpecialtyAndTitle}`;

        return clinicianPerson;
    }

    private static getFormattedSpecialtyAndTitle(specialty: string, title: string): string {
        return (!IsNullOrWhiteSpace(specialty) && !IsNullOrWhiteSpace(title))
            ? `(${specialty} - ${title})`
            : "";
    }
}

export { CliniciansHelper };
